import { Component } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import {
  CommentAPIModel,
  GovernmentBusinessAPIModel,
  LocationAPIModel,
  StatusAPIModel,
  UserAPIModel
} from '../../../models/api.model'
import { Subscription } from 'rxjs'
import { ActivatedRoute } from '@angular/router'
import { CommonService, formatDateToDDMMYYYY, formatDatetoYYYYMMDD } from '../../../services/common/common.service'
import { DocumentService } from '../../../services/common/document.service'
import { Store } from '@ngrx/store'
import { AppState } from '../../../store/state/app.state'
import { GovernmentBusinessService } from '../../../services/government/government-business.service'
import { GlobalConstants } from '../../../constants/global-constants'
import { addFormControl } from '../../../utils/form-control.util'
import { getUserFromState } from '../../../store/selector/initialize.selector'
import { ServiceUrlConstants } from '../../../constants/service-url-constants'
import { InitializeService } from '../../../services/initialize/initialize.service'

@Component({
  selector: 'app-government-business-details',
  templateUrl: './government-business-details.component.html',
  styleUrls: ['./government-business-details.component.scss']
})
export class GovernmentBusinessDetailsComponent {
  governmentBusinessDetailsForm: FormGroup
  governmentBusiness: GovernmentBusinessAPIModel
  commentsToSave: CommentAPIModel[] = []
  user$: Subscription
  user: UserAPIModel
  private id: string
  locationOptions: Map<string, string>

  readonly GOVERNMENT_BUSINESS_SUMMARY_LINK = `/${GlobalConstants.ROUTING.GOVERNMENT_BUSINESS}/${GlobalConstants.ROUTING.SUMMARY}`
  readonly USER_TYPE = GlobalConstants.USER_TYPE
  readonly ASSIGNED_TO_CONTROL_NAME = 'assignedTo'
  readonly RESPONSE_DUE_DATE_TIME_CONTROL_NAME = 'responseDueDatetime'
  private readonly CREATED_DATE_TIME_CONTROL_NAME = 'createdDatetime'
  readonly MODULE_NAME = GlobalConstants.MODULE.GOVERNMENT_BUSINESS_TASK
  private readonly STATUS_CONTROL_NAME = 'status'
  readonly LOCATION_CONTROL_NAME = 'location'
  filesToBeUploaded: File[]
  statusValueChange: boolean = false
  isDisabled: boolean = false
  hasFormChanged: boolean
  notify: boolean

  constructor(
    private governmentBusinessService: GovernmentBusinessService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private cs: CommonService,
    private documentService: DocumentService,
    private appState: Store<AppState>,
    private initializeService: InitializeService
  ) {}

  addAssignedToForm = addFormControl(this.ASSIGNED_TO_CONTROL_NAME, '')

  ngOnInit() {
    this.getUserDetailsFromStore()
    this.buildGovernmentBusinessDetailsForm()
    this.route.params.subscribe(params => {
      const id = params['id']
      this.id = id
      this.dispatchFetchGovernmentBusinessDetailsAction(id)
      this.getGovernmentBusinessDetailsFromStore()
    })
    this.getUserDetailsFromStore()
  }

  get f() {
    return this.governmentBusinessDetailsForm.controls
  }

  getUserDetailsFromStore = () => {
    this.user$ = this.appState.select(getUserFromState).subscribe((user: UserAPIModel) => {
      if (user) {
        this.user = user
        this.updateFields()
      }
    })
  }

  private buildGovernmentBusinessDetailsForm() {
    this.governmentBusinessDetailsForm = this.formBuilder.group({
      displayId: [{ value: '', disabled: true }],
      name: [{ value: '', disabled: true }],
      department: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(30)]],
      office: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(30)]],
      type: [{ value: '', disabled: true }],
      sectionReference: ['', [Validators.minLength(5), Validators.maxLength(40)]],
      [this.CREATED_DATE_TIME_CONTROL_NAME]: [{ value: '', disabled: true }],
      [this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]: [''],
      status: ['', [Validators.required]],
      detailedStatus: ['', [Validators.required]],
      description: [{ value: '', disabled: true }],
      statusUpdateFlag: [false],
      daysElapsed: [{ value: '', disabled: true }],
      companyEntityId: [],
      notify: [false],
      location: ['', [Validators.required]],
      taskCategory: [{ value: '', disabled: true }],
      taskSubCategory: [{ value: '', disabled: true }]
    })
  }

  private dispatchFetchGovernmentBusinessDetailsAction(id: any) {
    this.governmentBusinessService.dispatchFetchDetailsAction(id)
  }

  private getGovernmentBusinessDetailsFromStore() {
    this.governmentBusinessService.getGovernmentBusinessDetailsFromStore().subscribe(governmentBusiness => {
      if (governmentBusiness) {
        this.governmentBusiness = governmentBusiness
        this.governmentBusinessDetailsForm.patchValue({
          ...governmentBusiness,
          [this.CREATED_DATE_TIME_CONTROL_NAME]: formatDateToDDMMYYYY(governmentBusiness.createdDatetime)
        })
        this.updateFields()
      }
    })
  }

  addComment(commentAPIModel: CommentAPIModel) {
    this.commentsToSave = [commentAPIModel]
  }

  getIfNotifySelected(notify) {
    this.notify = notify || false
  }

  onStatusSelected(status: StatusAPIModel) {
    this.governmentBusinessDetailsForm.controls[this.STATUS_CONTROL_NAME].setValue(status.code)
  }

  handleAssignToSelectedValue(assignedTo: string) {
    this.governmentBusinessDetailsForm.patchValue({ assignedTo })
  }

  updateGovernmentBusinessDetails() {
    if (this.governmentBusinessDetailsForm.invalid) {
      return this.cs.showError('Invalid Input')
    }

    const commentToBeSaved = this.commentsToSave && this.commentsToSave[0]?.comment ? this.commentsToSave : []
    const notifyState = this.determineNotifyState(commentToBeSaved) || false

    this.governmentBusinessService.dispatchUpdateGovernmentBusinessAction(
      {
        ...this.governmentBusinessDetailsForm.value,
        companyEntityId: this.governmentBusiness.companyEntityId,
        statusUpdateFlag: this.statusValueChange,
        comments: commentToBeSaved,
        name: this.governmentBusiness.name,
        notify: this.statusValueChange ? false : notifyState
      },
      this.id,
      this.filesToBeUploaded,
      this.GOVERNMENT_BUSINESS_SUMMARY_LINK
    )
  }

  private updateFields() {
    const governmentBusiness = this.governmentBusiness
    if (governmentBusiness && this.user?.userType) {
      if (this.user.userType === this.USER_TYPE.RMS_ADMIN) {
        this.governmentBusinessDetailsForm.patchValue({
          [this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]: formatDatetoYYYYMMDD(this.governmentBusiness?.responseDueDatetime)
        })
      } else {
        this.governmentBusinessDetailsForm.patchValue({
          [this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]: formatDateToDDMMYYYY(this.governmentBusiness?.responseDueDatetime)
        })
        this.governmentBusinessDetailsForm.get(this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME).disable()
      }
      this.addAssignedToForm(this.governmentBusinessDetailsForm)
      this.loadLocationsForCompany(governmentBusiness?.companyEntityId)
      this.subscribeFormForValueChanges(this.governmentBusinessDetailsForm)
    }
  }

  downloadGovernmentBusiness() {
    const downloadEndPoint = ServiceUrlConstants.createDownloadExportPDFEndpoint('GOVERNMENT_BUSINESS', this.id)
    this.documentService.downloadDocument(downloadEndPoint)
  }

  handleFileInput(files: File[]) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  onStatusChange(flag: boolean) {
    this.statusValueChange = flag
    this.isDisabled = flag
  }

  mandatoryflag(flag: boolean) {
    if (this.statusValueChange && flag) {
      this.isDisabled = true
    } else {
      this.isDisabled = false
    }
  }

  private subscribeFormForValueChanges(formGroup: FormGroup) {
    const initialValue = formGroup?.value
    formGroup.valueChanges.subscribe(value => {
      this.hasFormChanged = Object.keys(initialValue).some(
        key => formGroup.value[key] != initialValue[key] && formGroup.dirty
      )
    })
  }

  determineNotifyState(commentToBeSaved: CommentAPIModel[]) {
    const hasCommented = commentToBeSaved?.length > 0
    const hasUploadedFiles = this.filesToBeUploaded !== null && this.filesToBeUploaded?.length > 0
    return ((this.hasFormChanged != null && this.hasFormChanged) || hasCommented || hasUploadedFiles) && this.notify
  }

  loadLocationsForCompany(companyEntityId: string) {
    if (companyEntityId) {
      this.initializeService.getRelatedLocationMap().subscribe({
        next: relatedLocationMap => {
          if (relatedLocationMap?.size > 0) {
            const locationAPIModelArr: LocationAPIModel[] = relatedLocationMap.get(companyEntityId)
            if (locationAPIModelArr) {
              const locationMap = new Map<string, string>()
              locationAPIModelArr?.forEach(locationAPIModel => {
                locationMap.set(locationAPIModel.code, locationAPIModel.code)
              })
              this.locationOptions = locationMap
            }
          }
        }
      })
    }
  }

  ngOnDestroy(): void {
    this.governmentBusinessService.dispatchDeleteGovernmentBusinessDetailsAction()
    this.user$?.unsubscribe()
  }
}
